import { Content, Header, Page } from '@backstage/core-components';
import { Tab, Tabs } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  llmProxyAppsKeyReadPermission,
  llmProxyAppsVirtualKeysPath,
  llmProxyLocaldevKeyReadPermission,
  llmProxyLocaldevVirtualKeysPath,
} from '@internal/plugin-llm-proxy-keys-common';
import { ChangeEvent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLlmProxyKeysConfig } from '../hooks';
import { AppsKeysTab } from './AppsKeysTab';
import { LocaldevKeysTab } from './LocaldevKeysTab';

type TabId = 'localdev' | 'apps';

function pathnameToTab(pathname: string): TabId | undefined {
  if (pathname === llmProxyLocaldevVirtualKeysPath) {
    return 'localdev';
  }
  if (pathname === llmProxyAppsVirtualKeysPath) {
    return 'apps';
  }
  return undefined;
}

function tabToPath(tab: TabId): string {
  return tab === 'localdev'
    ? llmProxyLocaldevVirtualKeysPath
    : llmProxyAppsVirtualKeysPath;
}

function defaultTabForPermissions(
  localdevAllowed: boolean,
  appsAllowed: boolean,
): TabId | undefined {
  if (localdevAllowed) {
    return 'localdev';
  }
  if (appsAllowed) {
    return 'apps';
  }
  return undefined;
}

export const LlmProxyKeysPage = () => {
  const { config, loading: configLoading } = useLlmProxyKeysConfig();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { allowed: localdevAllowed } = usePermission({
    permission: llmProxyLocaldevKeyReadPermission,
  });
  const { allowed: appsAllowed } = usePermission({
    permission: llmProxyAppsKeyReadPermission,
  });

  const activeTab = pathnameToTab(pathname);
  const defaultTab = defaultTabForPermissions(localdevAllowed, appsAllowed);

  useEffect(() => {
    if (!defaultTab || activeTab) {
      return;
    }
    navigate(tabToPath(defaultTab), { replace: true });
  }, [activeTab, defaultTab, navigate]);

  useEffect(() => {
    if (activeTab === 'apps' && !appsAllowed && localdevAllowed) {
      navigate(llmProxyLocaldevVirtualKeysPath, { replace: true });
    }
  }, [activeTab, appsAllowed, localdevAllowed, navigate]);

  if (configLoading) {
    return null;
  }

  if (!config?.enabled) {
    return (
      <Page themeId="tool">
        <Header title="LLM Proxy Keys" />
        <Content>
          <Alert severity="info">LLM Proxy Keys is not enabled.</Alert>
        </Content>
      </Page>
    );
  }

  if (!defaultTab) {
    return (
      <Page themeId="tool">
        <Header title="LLM Proxy Keys" />
        <Content>
          <Alert severity="error">
            You do not have permission to manage LLM proxy keys.
          </Alert>
        </Content>
      </Page>
    );
  }

  if (activeTab === 'apps' && !appsAllowed) {
    return (
      <Page themeId="tool">
        <Header title="LLM Proxy Keys" />
        <Content>
          <Alert severity="error">
            You do not have permission to manage apps LLM proxy keys.
          </Alert>
        </Content>
      </Page>
    );
  }

  if (activeTab === 'localdev' && !localdevAllowed) {
    return (
      <Page themeId="tool">
        <Header title="LLM Proxy Keys" />
        <Content>
          <Alert severity="error">
            You do not have permission to manage localdev LLM proxy keys.
          </Alert>
        </Content>
      </Page>
    );
  }

  const handleTabChange = (_event: ChangeEvent<{}>, tab: TabId) => {
    navigate(tabToPath(tab));
  };

  const showTabs = localdevAllowed && appsAllowed;

  return (
    <Page themeId="tool">
      <Header title="LLM Proxy Keys" />
      <Content>
        {showTabs && (
          <Tabs
            value={activeTab ?? defaultTab}
            onChange={handleTabChange}
            style={{ marginBottom: 16 }}
          >
            {localdevAllowed && <Tab label="Local Dev" value="localdev" />}
            {appsAllowed && <Tab label="Apps" value="apps" />}
          </Tabs>
        )}
        {activeTab === 'localdev' && <LocaldevKeysTab />}
        {activeTab === 'apps' && <AppsKeysTab />}
      </Content>
    </Page>
  );
};
